import React, { FC } from 'react';
import { Redirect } from 'react-router';
import * as paths from 'paths';
import { makeStyles } from '@material-ui/core/styles';
import { Dialog, DialogContent } from '@material-ui/core';

import { useGetSharePreviewInfoQuery, SharesItem } from 'api';
import { Loaders } from 'components';
import { useContent, useNotify } from 'hooks';
import { useCanShare } from 'features/content/hooks';

import { Heading, Form, Title, Transition } from '../components';

interface IProps {
  open: boolean;
  onClose: () => void;
  onDelete?: () => void;
  onUpdate?: () => void;
  existingShares?: SharesItem[];
}

const useStyles = makeStyles(theme => ({
  container: {
    paddingTop: theme.spacing(4),
  },
  content: {
    position: 'relative',
    paddingLeft: theme.spacing(10),
    paddingRight: theme.spacing(10),
    paddingTop: theme.spacing(5),
    backgroundColor: theme.palette.background.default,
  },
}));

const ShareDialog: FC<IProps> = props => {
  const { open, onClose } = props;

  const classes = useStyles();
  const notify = useNotify();
  const content = useContent();
  const canShare = useCanShare(content);

  const { data, isLoading, refetch } = useGetSharePreviewInfoQuery(
    { content_id: content.content_id },
    {
      onError: notify.queryError,
      enabled: open,
    }
  );

  function renderContent() {
    if (isLoading) {
      return <Loaders.Standard />;
    }

    if (!data?.getSharePreviewInfo) {
      return null;
    }

    const { getSharePreviewInfo } = data;

    if (!canShare) {
      return <Redirect to={paths.index} />;
    }

    return (
      <Form
        previewInfo={getSharePreviewInfo}
        onClose={onClose}
        onConnect={refetch}
        heading={
          <Heading
            shareCount={content.share_count}
            previewInfo={getSharePreviewInfo}
            onClose={onClose}
          />
        }
      />
    );
  }

  return (
    <Dialog
      fullScreen
      disableBackdropClick
      disableEscapeKeyDown
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
    >
      <Title />
      <DialogContent className={classes.content}>{renderContent()}</DialogContent>
    </Dialog>
  );
};

export default ShareDialog;
